import { useCallback, useEffect, useRef, useState } from 'react';

import { analyzeProject } from '../core/analysis';
import type { AnalysisReport, AnalyzeProjectInput } from '../types';
import { createAnalyzeWorkerRequest, isAnalysisWorkerResponse } from '../workers/protocol';

export type AnalysisStatus = 'idle' | 'running' | 'complete' | 'error';
export type AnalysisErrorCode = 'analysis-failed' | 'worker-unavailable-large-input';

const MAIN_THREAD_FALLBACK_PATH_LIMIT = 5_000;

export interface UseAnalysisResult {
  readonly report: AnalysisReport | null;
  readonly status: AnalysisStatus;
  readonly progress: number;
  readonly error: string | null;
  readonly errorCode: AnalysisErrorCode | null;
  readonly fallbackUsed: boolean;
  readonly rerun: () => void;
}

let requestSequence = 0;

export function createAnalysisWorker(): Worker {
  return new Worker(new URL('../workers/analysis.worker.ts', import.meta.url), {
    type: 'module',
    name: 'what-the-ignore-analysis',
  });
}

export function useAnalysis(input: AnalyzeProjectInput | null | undefined): UseAnalysisResult {
  const [report, setReport] = useState<AnalysisReport | null>(null);
  const [status, setStatus] = useState<AnalysisStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<AnalysisErrorCode | null>(null);
  const [fallbackUsed, setFallbackUsed] = useState(false);
  const [runVersion, setRunVersion] = useState(0);
  const workerRef = useRef<Worker | null>(null);

  const rerun = useCallback(() => {
    setRunVersion((version) => version + 1);
  }, []);

  useEffect(() => {
    workerRef.current?.terminate();
    workerRef.current = null;
    let cancelled = false;
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

    if (input === null || input === undefined) {
      queueMicrotask(() => {
        if (!cancelled) {
          setReport(null);
          setStatus('idle');
          setProgress(0);
          setError(null);
          setErrorCode(null);
          setFallbackUsed(false);
        }
      });
      return () => {
        cancelled = true;
      };
    }

    let fallbackStarted = false;
    const requestId = `analysis-${String(++requestSequence)}`;

    queueMicrotask(() => {
      if (!cancelled) {
        setReport(null);
        setStatus('running');
        setProgress(0);
        setError(null);
        setErrorCode(null);
        setFallbackUsed(false);
      }
    });

    const runFallback = (): void => {
      if (fallbackStarted || cancelled) {
        return;
      }
      fallbackStarted = true;
      workerRef.current?.terminate();
      workerRef.current = null;

      queueMicrotask(() => {
        if (cancelled) {
          return;
        }
        setFallbackUsed(true);
        setProgress(0);
        if (input.paths.length > MAIN_THREAD_FALLBACK_PATH_LIMIT) {
          setError('Worker unavailable for a large input.');
          setErrorCode('worker-unavailable-large-input');
          setStatus('error');
          return;
        }

        fallbackTimer = setTimeout(() => {
          if (cancelled) {
            return;
          }
          try {
            const nextReport = analyzeProject(input);
            setProgress(100);
            setReport(nextReport);
            setStatus('complete');
          } catch (fallbackError: unknown) {
            setError(toErrorMessage(fallbackError));
            setErrorCode('analysis-failed');
            setStatus('error');
          }
        }, 0);
      });
    };

    if (typeof Worker === 'undefined') {
      runFallback();
      return () => {
        cancelled = true;
        if (fallbackTimer !== null) {
          clearTimeout(fallbackTimer);
        }
      };
    }

    let worker: Worker;
    try {
      worker = createAnalysisWorker();
      workerRef.current = worker;
    } catch {
      runFallback();
      return () => {
        cancelled = true;
        if (fallbackTimer !== null) {
          clearTimeout(fallbackTimer);
        }
      };
    }

    const handleMessage = (event: MessageEvent<unknown>): void => {
      if (
        cancelled ||
        !isAnalysisWorkerResponse(event.data) ||
        event.data.requestId !== requestId
      ) {
        return;
      }

      if (event.data.type === 'progress') {
        setProgress(progressPercent(event.data.progress.completed, event.data.progress.total));
        return;
      }

      worker.terminate();
      workerRef.current = null;
      if (event.data.type === 'success') {
        setReport(event.data.report);
        setProgress(100);
        setStatus('complete');
        return;
      }

      setError(event.data.error.message);
      setErrorCode('analysis-failed');
      setStatus('error');
    };

    const handleWorkerFailure = (): void => {
      runFallback();
    };

    worker.addEventListener('message', handleMessage);
    worker.addEventListener('error', handleWorkerFailure);
    worker.addEventListener('messageerror', handleWorkerFailure);

    try {
      worker.postMessage(createAnalyzeWorkerRequest(requestId, input));
    } catch {
      runFallback();
    }

    return () => {
      cancelled = true;
      worker.removeEventListener('message', handleMessage);
      worker.removeEventListener('error', handleWorkerFailure);
      worker.removeEventListener('messageerror', handleWorkerFailure);
      worker.terminate();
      if (workerRef.current === worker) {
        workerRef.current = null;
      }
      if (fallbackTimer !== null) {
        clearTimeout(fallbackTimer);
      }
    };
  }, [input, runVersion]);

  return { report, status, progress, error, errorCode, fallbackUsed, rerun };
}

function toErrorMessage(value: unknown): string {
  if (value instanceof Error) {
    return value.message;
  }
  return typeof value === 'string' ? value : 'Analysis failed.';
}

function progressPercent(completed: number, total: number): number {
  if (total <= 0) {
    return completed > 0 ? 100 : 0;
  }
  return Math.max(0, Math.min(100, Math.round((completed / total) * 100)));
}
